import { useState, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  knowledgeBasesCreateUploadSession,
  knowledgeBasesLocalUpload,
  knowledgeBasesCompleteUpload,
} from "../sdk";
import { uploadToGCSResumable } from "../utils/gcs-upload";
import { queryKeys } from "../query/keys";
import { coerceToString } from "../utils/errors";

export interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
  id?: string;
}

function getErrorMessage(error: unknown): string {
  return coerceToString(error, "Failed to upload file");
}

/**
 * Hook for uploading files to a knowledge base using resumable uploads.
 *
 * 3-step flow:
 * 1. POST create-upload-session -> get file_id + resumable_url (or local indicator)
 * 2. PUT chunks to GCS resumable URL (or POST to local-upload endpoint)
 * 3. POST complete-upload -> verify blob, create job, enqueue Celery
 *
 * @param orgId - Organization ID
 * @param kbId - Knowledge Base ID
 * @param folderId - Optional folder ID to upload to
 * @param onUploadComplete - Optional callback called after each successful upload
 * @returns Upload functions and state (same public API as before)
 */
export function useKBUpload(
  orgId: string,
  kbId: string | undefined,
  folderId?: string | null,
  onUploadComplete?: () => void | Promise<void>
) {
  const queryClient = useQueryClient();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllers = useRef<Map<File, AbortController>>(new Map());

  const uploadFile = async (file: File): Promise<{ file_id?: string; job_id?: string } | null> => {
    if (!orgId || !kbId) {
      toast.error("Invalid organization or knowledge base");
      return null;
    }

    // Add to uploading list
    setUploadingFiles((prev) => [
      ...prev,
      { file, progress: 0 },
    ]);

    const abortController = new AbortController();
    abortControllers.current.set(file, abortController);

    try {
      // Step 1: Create upload session
      const sessionResponse = await knowledgeBasesCreateUploadSession({
        path: { org_id: orgId, kb_id: kbId },
        body: {
          file_name: file.name,
          file_size: file.size,
          content_type: file.type || "application/octet-stream",
          folder_id: folderId || undefined,
        },
      });

      if (sessionResponse.error) {
        throw new Error(
          coerceToString(
            sessionResponse.error,
            "Failed to create upload session.",
          ),
        );
      }
      const session = sessionResponse.data;
      if (!session) throw new Error("Upload session response was empty.");

      const fileId = session.file_id;

      // Update progress to show session created
      setUploadingFiles((prev) =>
        prev.map((uf) =>
          uf.file === file ? { ...uf, progress: 5, id: fileId } : uf
        )
      );

      // Step 2: Upload file content
      if (session.storage_backend === "gcs" && session.resumable_url) {
        // Production: chunked upload directly to GCS
        await uploadToGCSResumable(
          session.resumable_url,
          file,
          file.size,
          (progress) => {
            // Scale progress: 5% (session) + 90% (upload) + 5% (complete)
            const scaled = 5 + Math.round(progress.percentage * 0.9);
            setUploadingFiles((prev) =>
              prev.map((uf) =>
                uf.file === file ? { ...uf, progress: scaled } : uf
              )
            );
          },
          abortController.signal,
        );
      } else {
        // Local dev: POST file to backend
        const localUploadResponse = await knowledgeBasesLocalUpload({
          path: { org_id: orgId, kb_id: kbId, file_id: fileId },
          body: { file },
        });

        if (localUploadResponse.error) {
          throw new Error(
            coerceToString(
              localUploadResponse.error,
              "Failed to upload file bytes to the backend.",
            ),
          );
        }

        setUploadingFiles((prev) =>
          prev.map((uf) =>
            uf.file === file ? { ...uf, progress: 95 } : uf
          )
        );
      }

      // Step 3: Complete upload (verify blob + enqueue processing)
      const completeResponse = await knowledgeBasesCompleteUpload({
        path: { org_id: orgId, kb_id: kbId, file_id: fileId },
      });

      if (completeResponse.error) {
        throw new Error(
          coerceToString(
            completeResponse.error,
            "Failed to finalize the upload.",
          ),
        );
      }
      const result = completeResponse.data;

      if (kbId && result) {
        queryClient.setQueryData(
          queryKeys.kbFiles.list({ kbId, folderId }),
          (existingFiles: Array<Record<string, unknown>> | undefined) => {
            if (!existingFiles) {
              return existingFiles;
            }

            // CompleteUploadResponse carries `file_id`, not `id` — match
            // against the generated file id that both sides share.
            return existingFiles.map((existingFile) =>
              existingFile.id === result.file_id
                ? {
                    ...existingFile,
                    ...result,
                  }
                : existingFile
            );
          }
        );
      }

      // Update progress to 100%
      setUploadingFiles((prev) =>
        prev.map((uf) =>
          uf.file === file ? { ...uf, progress: 100, id: fileId } : uf
        )
      );

      // Remove from uploading list after a short delay
      setTimeout(() => {
        setUploadingFiles((prev) => prev.filter((uf) => uf.file !== file));
      }, 2000);

      toast.success(`File "${file.name}" uploaded successfully`);

      // Invalidate caches. The org-wide KB list carries per-KB
      // file_count / size_bytes that the index page surfaces on each
      // tile, so we have to invalidate `knowledgeBases.lists()` too —
      // otherwise the tile stats stay stale until the next refresh.
      if (kbId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kbFiles.list({ kbId, folderId }),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kbFolders.list(kbId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.knowledgeBases.detail(kbId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.knowledgeBases.lists(),
        });
      }

      if (onUploadComplete) {
        await onUploadComplete();
      }

      return result || null;
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        setUploadingFiles((prev) => prev.filter((uf) => uf.file !== file));
        return null;
      }

      const errorMessage = getErrorMessage(err);

      setUploadingFiles((prev) =>
        prev.map((uf) =>
          uf.file === file
            ? { ...uf, progress: 0, error: errorMessage }
            : uf
        )
      );

      toast.error(errorMessage);
      return null;
    } finally {
      abortControllers.current.delete(file);
    }
  };

  /**
   * Cap concurrent KB uploads. Each file owns a `kb_files` row that
   * stays in UPLOADING until step-3 `complete_upload` fires. Browsers
   * cap ~6 concurrent HTTP/1.1 connections per origin, and every GCS
   * resumable PUT counts against that budget. Firing Promise.all on
   * 10+ files stacks the queue, stalls some, and the first one that
   * times out / hits GCS 503 / AbortErrors will skip its own step 3,
   * leaving a permanent UPLOADING orphan. A small pool keeps the
   * network calm and the error rate predictable. The maintenance-task
   * sweeper (`recover_stuck_kb_processing`) catches any remaining
   * orphans after 15 min.
   */
  const UPLOAD_CONCURRENCY = 3;

  const uploadFiles = async (
    files: File[]
  ): Promise<({ file_id?: string; job_id?: string } | null)[]> => {
    setIsUploading(true);
    const results: ({ file_id?: string; job_id?: string } | null)[] = new Array(
      files.length,
    ).fill(null);

    let nextIndex = 0;
    const worker = async () => {
      while (true) {
        const index = nextIndex;
        nextIndex += 1;
        if (index >= files.length) return;
        results[index] = await uploadFile(files[index]!);
      }
    };

    const workerCount = Math.min(UPLOAD_CONCURRENCY, files.length);
    await Promise.all(
      Array.from({ length: workerCount }, () => worker()),
    );

    setIsUploading(false);
    return results;
  };

  const cancelUpload = (file: File) => {
    const controller = abortControllers.current.get(file);
    if (controller) {
      controller.abort();
    }
    setUploadingFiles((prev) => prev.filter((uf) => uf.file !== file));
    toast.info(`Upload of "${file.name}" cancelled`);
  };

  const clearUploadingFiles = () => {
    setUploadingFiles([]);
  };

  return {
    uploadingFiles,
    isUploading,
    uploadFile,
    uploadFiles,
    cancelUpload,
    clearUploadingFiles,
  };
}
